import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import { ADMIN_USER_LOADING } from "../../constants/loading.constant";
import {
  USER_EXPAND_CREATE_FORM,
  USER_EXPAND_EDIT_FORM,
} from "../../constants/modal.constant";
import { useConfigCore } from "../../cores/config";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";
import { useRouterCore } from "../../cores/router";
import { User } from "../../cores/session/interfaces/models/user";
import { useUserCore } from "../../cores/user";
import {
  SortDirection,
  UserSortField,
} from "../../cores/user/interfaces/api/get-users";
import EditUserForm from "../component/admin/users/EditUserForm";
import { UsersBar } from "../component/admin/users/UsersBar";
import { FreeTrialBadge } from "../component/companies/FreeTrialBadge";
import { PlanBadge } from "../component/companies/PlanBadge";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import UserRow from "../component/UserRow";
import { ArchiveCandidatesModal } from "../component/admin/users/ArchiveCandidatesModal";
import UsersTable from "../component/admin/users/UsersTable";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";

export default function Users() {
  const intl = useIntl();
  const { search } = useLocation();
  const { users, total, getUser } = useUserCore();
  const [usersView, setUsersView] = useState<"cards" | "table">("table");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<UserSortField>("lastLoginAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("DESC");
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [usersRefreshToken, setUsersRefreshToken] = useState(0);
  const { getConfig } = useConfigCore();
  const { user } = useMeCore();
  const { turnOnModal } = useGlobalCore();
  const { goToSocial, goToUsers } = useRouterCore();
  const openedEmailRef = useRef<string | null>(null);

  const profileEmail = useMemo(() => {
    const params = new URLSearchParams(search);
    return String(params.get("email") || "").trim();
  }, [search]);

  useEffect(() => {
    getConfig();
  }, [getConfig]);
  
  useEffect(() => {
    if (!user?.userRole?.admin && !(user?.userRole?.reseller && user?.reseller))
      goToSocial();
  }, [user, goToSocial]);

  const onClick = useCallback((user: User) => {
    turnOnModal(USER_EXPAND_EDIT_FORM, <EditUserForm user={user} />);
  }, [turnOnModal]);

  useEffect(() => {
    if (!profileEmail) {
      openedEmailRef.current = null;
      return;
    }
    if (openedEmailRef.current === profileEmail.toLowerCase()) return;

    let cancelled = false;
    (async () => {
      const found = await getUser(profileEmail);
      if (cancelled) return;
      if (!found) {
        toast.error("No se pudo abrir el perfil del usuario");
        goToUsers();
        return;
      }
      openedEmailRef.current = profileEmail.toLowerCase();
      turnOnModal(USER_EXPAND_EDIT_FORM, <EditUserForm user={found} />);
      goToUsers();
    })();

    return () => {
      cancelled = true;
    };
  }, [profileEmail, getUser, turnOnModal, goToUsers]);

  useEffect(() => {
    setSelectedUserIds((prev) => prev.filter((id) => users.some((user) => user.id === id)));
  }, [users]);

  useEffect(() => {
    if (usersView !== "table") setSelectedUserIds([]);
  }, [usersView]);

  const toggleUserSelection = useCallback((userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }, []);

  const toggleAllVisibleUsers = useCallback(() => {
    const visibleIds = users.map(({ id }) => id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedUserIds.includes(id));

    setSelectedUserIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  }, [selectedUserIds, users]);

  const isSuperAdmin = Boolean(user?.userRole?.admin && !user?.userRole?.reseller);

  const changeSort = useCallback(
    (field: UserSortField) => {
      if (field === sortBy) {
        setSortDirection((current) => (current === "ASC" ? "DESC" : "ASC"));
        return;
      }
      setSortBy(field);
      setSortDirection("DESC");
    },
    [sortBy]
  );

  return (
    <Layout>
      <Header2
        title={`${intl.formatMessage({ id: "app.adminUsers.title" })} (${total})`}
        rightChildren={
          <div className="d-flex flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
            {isSuperAdmin && (
              <Button
                color="warning"
                outline
                onClick={() => setArchiveModalOpen(true)}
              >
                {intl.formatMessage({
                  id: "app.users.archiveCandidates.open",
                  defaultMessage: "Archivar candidatos",
                })}
              </Button>
            )}
            <Button
              className="whiteButton"
              onClick={() => turnOnModal(USER_EXPAND_CREATE_FORM, true)}
            >
              {intl.formatMessage({ id: "app.adminUsers.creatUser" })}
            </Button>
          </div>
        }
      />
      <Content>
        <Container fluid>
          <Row>
            <Col xs="12">
              <UsersBar
                usersView={usersView}
                onUsersViewChange={setUsersView}
                selectedUserIds={selectedUserIds}
                onClearSelection={() => setSelectedUserIds([])}
                sortBy={sortBy}
                sortDirection={sortDirection}
                usersRefreshToken={usersRefreshToken}
              />
              <ArchiveCandidatesModal
                isOpen={archiveModalOpen}
                onClose={() => setArchiveModalOpen(false)}
                onArchived={() => setUsersRefreshToken((value) => value + 1)}
              />
              <UpdateLoadingCard
                loadingId={ADMIN_USER_LOADING}
                text={intl.formatMessage({ id: "app.adminUsers.updatingUser" })}
              />
              <Container fluid>
                {usersView === "table" ? (
                  <UsersTable
                    users={users}
                    onEditUser={onClick}
                    selectedIds={selectedUserIds}
                    onToggleUser={toggleUserSelection}
                    onToggleAllUsers={toggleAllVisibleUsers}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={changeSort}
                  />
                ) : (
                  users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      actions={
                        <div className="d-flex justify-content-center align-items-center flex-wrap">
                          {u.freeTrial && (
                            <FreeTrialBadge freeTrial={u.freeTrial} />
                          )}
                          <PlanBadge plan={u.userPlan!} show />
                        </div>
                      }
                      onClick={() => onClick(u)}
                    />
                  ))
                )}
              </Container>
            </Col>
          </Row>
        </Container>
      </Content>
    </Layout>
  );
}
